import { SmartSalesConstants } from '../smartSales/SmartSalesConstants';
import { ActionType } from '../models/Action';
export class SmartSalesService {
    static $inject = ['CallerConfigurationService', '$translate', 'TenantService', 'PipelineBuilderStateService', '$http', 'ApplicationService2'];
    constructor(CallerConfigurationService, $translate, TenantService, PipelineBuilderStateService, $http, ApplicationService2) {
        'ngInject';
        this.CallerConfigurationService = CallerConfigurationService;
        this.$translate = $translate;
        this.TenantService = TenantService;
        this.PipelineBuilderStateService = PipelineBuilderStateService;
        this.$http = $http;
        this.ApplicationService2 = ApplicationService2;
    }
    setApplication(application, useConfigurationsApplication = false) {
        const identity = this.getBotIdentityByShortName(application.shortName);
        this.application = { ...application, identity };
        if (!this.smartSalesOwner) {
            this.setSmartSalesOwnerAsync(useConfigurationsApplication);
        }
    }
    async setSmartSalesOwnerAsync(useConfigurationsApplication = false) {
        this.smartSalesOwner = await this.getSmartSalesOwner(useConfigurationsApplication);
    }
    async createSmartSalesState() {
        if (!this.smartSalesOwner) {
            return;
        }
        this.token = await this.getApplicationToken(this.smartSalesOwner);
        return this.PipelineBuilderStateService.createSmartSalesState(await this.$translate('builder.node.defaultSmartSalesTitle'), this.token);
    }
    async setSmartSalesStateInformation(settings) {
        await this.$http({
            method: 'POST',
            headers: {
                Token: this.token,
            },
            url: `${SmartSalesConstants.API_BASE_URL}/Bot/state`,
            data: {
                ...settings,
            },
        });
    }
    async deleteSmartSalesState(stateId) {
        await this.$http({
            method: 'DELETE',
            headers: {
                Token: this.token,
            },
            url: `${SmartSalesConstants.API_BASE_URL}/Bot/state/${stateId}`,
        });
    }
    async getSmartSalesOwner(useConfigurationsApplication = false) {
        const router = await this.searchRouterWithSmartSalesAndBotConnection(useConfigurationsApplication);
        if (router) {
            return router;
        }
        const botConfigurations = await this.CallerConfigurationService.get(this.application.identity, useConfigurationsApplication);
        const installedInBot = this.hasSmartSalesInstalled(botConfigurations?.items);
        if (installedInBot) {
            return {
                identifier: this.application.shortName,
                accessKey: this.application.accessKey,
            };
        }
    }
    async redirectToInstallPlugin() {
        const linkToInstallPlugin = await this.$translate('builder.smartSales.linkToInstall');
        window.location.href = linkToInstallPlugin;
    }
    setTokenApplicationByState(state) {
        const processHttpAction = state.$enteringCustomActions.find(action => action.type === ActionType.ProcessHttp);
        this.token = processHttpAction?.settings?.headers?.Token;
    }
    async createUserSession(identity) {
        if (this.userSession !== undefined) {
            return this.userSession;
        }
        const { data } = await this.$http({
            method: 'GET',
            url: `${SmartSalesConstants.API_BASE_URL}/Bot/Catalog`,
            headers: {
                Token: this.token,
                Identity: identity,
            },
        });
        this.userSession = data;
        return data;
    }
    mergeSmartSalesState(newSmarSalesState, oldSmartSalesState) {
        const oldCtaValues = oldSmartSalesState.$enteringCustomActions.find(action => action.$title === SmartSalesConstants.WA_CTA_ACTION_NAME);
        oldSmartSalesState.id = newSmarSalesState.id;
        oldSmartSalesState.$enteringCustomActions = newSmarSalesState.$enteringCustomActions;
        oldSmartSalesState.$leavingCustomActions = newSmarSalesState.$leavingCustomActions;
        oldSmartSalesState.stateVersion = newSmarSalesState.stateVersion;
        if (oldCtaValues) {
            oldSmartSalesState.$enteringCustomActions = oldSmartSalesState.$enteringCustomActions.map((currentAction) => {
                if (currentAction.$title === oldCtaValues.$title) {
                    return oldCtaValues;
                }
                return currentAction;
            });
        }
        return oldSmartSalesState;
    }
    isSmartSalesStateVersionUpToDate(stateVersion) {
        if (!stateVersion) {
            return false;
        }
        return parseFloat(stateVersion) >= parseFloat(SmartSalesConstants.CURRENT_SMART_SALES_VERSION);
    }
    getCtaInformation(state) {
        const ctaAction = state.$enteringCustomActions.find(action => action.$title === SmartSalesConstants.WA_CTA_ACTION_NAME);
        if (ctaAction) {
            return JSON.parse(ctaAction.settings.value);
        }
    }
    getSmartSalesWebLinkEnabled(state) {
        const waWebAction = state.$enteringCustomActions.find(action => action.$title === SmartSalesConstants.WA_WEB_ACTION_NAME);
        if (waWebAction) {
            return JSON.parse(waWebAction.settings.value);
        }
    }
    isStateVersionAvailableToCta(stateVersion) {
        return parseFloat(stateVersion) >= parseFloat(SmartSalesConstants.SMART_SALES_VERSION_WITH_CTA);
    }
    isStateVersionAvailableToCtaToggle(stateVersion) {
        return parseFloat(stateVersion) >= parseFloat(SmartSalesConstants.SMART_SALES_VERSION_WITH_CTA_TOGGLE);
    }
    async searchRouterWithSmartSalesAndBotConnection(useConfigurationsApplication = false) {
        const tenantBots = await this.TenantService.getBotsByTenantId();
        const routers = tenantBots.items.filter(bot => bot.template === SmartSalesConstants.ROUTER_NAME &&
            !!bot.hasPermission);
        const promises = [];
        routers.forEach(router => promises.push(this.CallerConfigurationService.get(router.shortName + SmartSalesConstants.ROUTER_DOMAIN, useConfigurationsApplication)));
        const responses = await Promise.all(promises.map(p => p.catch(error => error)));
        for (const currentRouter of responses) {
            if (!this.hasSmartSalesInstalled(currentRouter?.items)) {
                continue;
            }
            const application = this.getBotApplication(currentRouter.items);
            if (!application?.value) {
                continue;
            }
            const { identifier, settings: { children }, } = JSON.parse(application.value);
            const connectionWithBot = children?.find(bot => bot.identity === this.application.identity);
            if (connectionWithBot) {
                const accessKey = await this.ApplicationService2
                    .getApplication(identifier)
                    .then(app => app.accessKey);
                return {
                    identifier,
                    accessKey,
                };
            }
        }
    }
    hasSmartSalesInstalled(items) {
        const plugins = items?.find(item => item.name === SmartSalesConstants.PLUGINS_NAME);
        if (!plugins?.value) {
            return false;
        }
        return Object.keys(JSON.parse(plugins.value)).includes(SmartSalesConstants.SMART_SALES_PLUGIN_ID);
    }
    getBotApplication(items) {
        return items?.find(item => item.name === SmartSalesConstants.APPLICATION_NAME);
    }
    async getApplicationToken({ accessKey, identifier, }) {
        const { data } = await this.$http({
            method: 'POST',
            url: `${SmartSalesConstants.API_BASE_URL}/Token`,
            data: {
                accessKey,
                identifier,
                tenant: this.TenantService.getTenantPrefixFromUrl(),
            },
        });
        return data;
    }
    getBotIdentityByShortName(shortName) {
        const BOT_DOMAIN = '@msging.net';
        if (!shortName.includes(BOT_DOMAIN)) {
            return shortName + BOT_DOMAIN;
        }
        return shortName;
    }
}
SmartSalesService.$inject = ['CallerConfigurationService', '$translate', 'TenantService', 'PipelineBuilderStateService', '$http', 'ApplicationService2'];
